import { useEffect, useState } from "react";
import { CreditCard, BellRing } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { FadeUp } from "../components/Reveal";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const DEPOSIT_BADGE = {
  paid: "bg-emerald-100 text-emerald-800",
  unpaid: "bg-amber-100 text-amber-800",
  waived: "bg-slate-100 text-slate-600",
};

function Stat({ label, value, dark, testId }) {
  return (
    <div className={`rounded-3xl p-6 ${dark ? "bg-obsidian text-white grain" : "bg-white border border-purple-100 text-obsidian"}`} data-testid={testId}>
      <p className={`text-[10px] font-mono uppercase tracking-widest ${dark ? "text-white/40" : "text-slate-400"}`}>{label}</p>
      <p className="font-serif text-3xl sm:text-4xl mt-2">{value}</p>
    </div>
  );
}

export default function AdminPayments() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/payments").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runReminders = async () => {
    try { const r = await api.post("/admin/reminders/run"); toast.success(r.data.sent ? `${r.data.sent} reminder text(s) sent` : "No sessions tomorrow need a reminder"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (!data) return null;
  const { summary, bookings } = data;

  return (
    <div data-testid="admin-payments">
      <FadeUp className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl text-obsidian">Payments & <span className="italic text-purple-700">balances</span></h1>
          <p className="text-sm text-slate-500 mt-2 flex items-center gap-2"><CreditCard size={14} className="text-gold" /> Deposits collected through Stripe, what's still owed, and the balance due at each session.</p>
        </div>
        <button onClick={runReminders} data-testid="run-reminders-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-obsidian text-white text-xs hover:text-gold transition-colors">
          <BellRing size={13} /> Send tomorrow's reminders now
        </button>
      </FadeUp>

      <div className="mt-10 grid gap-4 sm:grid-cols-3">
        <Stat label="Deposits collected" value={money(summary.collected)} dark testId="payments-collected" />
        <Stat label="Deposits outstanding" value={money(summary.outstanding_deposits)} testId="payments-outstanding" />
        <Stat label="Balances due at session" value={money(summary.balances_due)} testId="payments-balances" />
      </div>

      <FadeUp delay={0.1} className="mt-8 bg-white rounded-3xl border border-purple-100 overflow-hidden" data-testid="payments-table">
        <div className="hidden md:grid grid-cols-12 gap-3 px-6 py-3 text-[10px] font-mono uppercase tracking-widest text-slate-400 border-b border-purple-50">
          <span className="col-span-3">Client</span><span className="col-span-3">Session</span><span className="col-span-2 text-right">Price</span><span className="col-span-2 text-right">Paid</span><span className="col-span-2 text-right">Balance</span>
        </div>
        {bookings.map((b) => (
          <div key={b.booking_id} className="grid md:grid-cols-12 gap-2 md:gap-3 px-6 py-4 border-b border-purple-50 last:border-0 text-sm items-center" data-testid={`payment-row-${b.booking_id}`}>
            <div className="md:col-span-3">
              <p className="text-obsidian font-medium">{b.client_name}</p>
              <p className="text-xs text-slate-400">{b.client_email}</p>
            </div>
            <div className="md:col-span-3">
              <p className="text-obsidian">{b.service_name}</p>
              <p className="text-xs text-slate-400">{b.date} · {b.time} · {b.location_name}</p>
            </div>
            <p className="md:col-span-2 md:text-right text-obsidian">{money(b.price)}</p>
            <div className="md:col-span-2 md:text-right">
              <p className="text-obsidian">{money(b.paid)}</p>
              <span className={`inline-block mt-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${DEPOSIT_BADGE[b.deposit_status] || DEPOSIT_BADGE.waived}`}>
                {b.deposit_status === "paid" ? `${b.deposit_percent ?? 50}% deposit paid` : b.deposit_status === "unpaid" ? `${b.deposit_percent ?? 50}% deposit due` : "no deposit"}
              </span>
            </div>
            <p className={`md:col-span-2 md:text-right font-medium ${b.balance > 0 ? "text-obsidian" : "text-emerald-700"}`}>{b.balance > 0 ? money(b.balance) : "Paid in full"}</p>
          </div>
        ))}
        {bookings.length === 0 && <p className="p-8 text-sm text-slate-400 italic">No approved sessions yet.</p>}
      </FadeUp>
    </div>
  );
}
